import { z } from "zod";
import { event, notification, request } from "./defs.ts";
import { projectIdSchema } from "./schemas.ts";

/**
 * LSP transport (M6.1). The OUTER envelope is strict; the inner `message` is standard LSP JSON-RPC that racler does not
 * re-implement (it only requires a JSON-RPC 2.0 object). A browser can never provide an executable, argv or a path:
 * it names a language family from the Agent's own registry and a Project; the Agent chooses everything else.
 *
 * URIs inside `message` are SYNTHETIC (`racler-project://<projectId>/<relative path>`); the Agent rewrites them to real
 * `file://` URIs for the language server and back, so a Machine's absolute Project path never reaches server/browser.
 */
export const LSP_URI_SCHEME = "racler-project";
/** Placeholder scheme for locations the language server reports OUTSIDE the Project (never a real path). */
export const LSP_EXTERNAL_URI = "racler-external://outside";

export const lspSessionIdSchema = z.uuid();
export const lspLanguageIdSchema = z
  .string()
  .min(1)
  .max(32)
  .regex(/^[a-z][a-z0-9-]*$/);

export const lspMessageSchema = z.looseObject({ jsonrpc: z.literal("2.0") });
export type LspMessage = z.infer<typeof lspMessageSchema>;

export const lspStatePhases = ["ready", "unavailable", "stopped", "crashed"] as const;
export type LspState = (typeof lspStatePhases)[number];

export const lspStartRequest = request(
  "lsp",
  "lsp.start",
  z.strictObject({
    sessionId: lspSessionIdSchema,
    projectId: projectIdSchema,
    languageId: lspLanguageIdSchema,
  }),
  z.strictObject({
    sessionId: lspSessionIdSchema,
    state: z.enum(["ready", "unavailable"]),
    languageId: lspLanguageIdSchema,
    reason: z.string().max(500).optional(),
  }),
);

export const lspStatusRequest = request(
  "lsp",
  "lsp.status",
  z.strictObject({ sessionId: lspSessionIdSchema }),
  z.strictObject({ state: z.enum(lspStatePhases) }),
);

/** browser → agent */
export const lspMessageNotification = notification(
  "lsp",
  "lsp.message",
  z.strictObject({ sessionId: lspSessionIdSchema, message: lspMessageSchema }),
);

/** agent → browser (only to the browser that owns the session) */
export const lspMessageEvent = event(
  "lsp",
  "lsp.message",
  z.strictObject({ sessionId: lspSessionIdSchema, message: lspMessageSchema }),
);

export const lspStatusEvent = event(
  "lsp",
  "lsp.status",
  z.strictObject({
    sessionId: lspSessionIdSchema,
    state: z.enum(["stopped", "crashed"]),
    message: z.string().max(500).optional(),
  }),
);

/** browser → agent (fire-and-forget; the relay also sends it when a browser disconnects). */
export const lspStopNotification = notification(
  "lsp",
  "lsp.stop",
  z.strictObject({ sessionId: lspSessionIdSchema }),
);

export const lspRequests = {
  "lsp/lsp.start": lspStartRequest,
  "lsp/lsp.status": lspStatusRequest,
} as const;
export const lspNotifications = {
  "lsp/lsp.message": lspMessageNotification,
  "lsp/lsp.stop": lspStopNotification,
} as const;
export const lspEvents = { "lsp/lsp.message": lspMessageEvent, "lsp/lsp.status": lspStatusEvent } as const;
